import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useNavItems, type NavItem } from "@/config/navItems";
import { SETUP_ROUTE_PERMISSIONS } from "@/config/setupAccess";
import PermissionBoundary from "./PermissionBoundary";

/** Direct URLs use the same access policy as navigation, including nested routes. */
export default function NavigationPermissionBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const items = useNavItems(false);
  let currentPath = pathname.toLowerCase();
  try { currentPath = decodeURIComponent(pathname).toLowerCase(); } catch { /* Router handles malformed URLs. */ }
  const policies: Record<string, string[][]> = {};
  const collect = (nodes: NavItem[], inherited: string[][] = []) => {
    for (const item of nodes) {
      const requirements = item.permissions?.length ? [...inherited, item.permissions] : inherited;
      const path = item.route?.split(/[?#]/)[0].toLowerCase();
      if (path && (requirements.length || !policies[path])) policies[path] = requirements;
      if (item.children) collect(item.children, requirements);
    }
  };
  collect(items);
  for (const [route, codes] of Object.entries(SETUP_ROUTE_PERMISSIONS)) policies[route] = [codes];
  const route = Object.keys(policies).sort((a, b) => b.length - a.length)
    .find((path) => currentPath === path || currentPath.startsWith(`${path}/`));
  return <>{(route ? policies[route] : []).reduceRight<ReactNode>(
    (content, permissions) => <PermissionBoundary permissions={permissions}>{content}</PermissionBoundary>, children,
  )}</>;
}
